// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import React, { useState, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Tooltip,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
  Backdrop,
  Button,
  LinearProgress,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { AppConfig, roles, FILE_UPLOAD_LIMIT, LEGAL_CHOOSE_FILE_SUBQUESTION_ID } from "@configs/admin-config";
import Avatar from "@mui/material/Avatar";
import { getAccessToken, getUserName, checkIfRoleExists } from "@utils/oauth";
import useHttp from "@utils/http";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  parentDiv: {
    marginBottom: 15,
  },
  questionParent: {
    display: "flex",
  },
  questionIndex: {
    flexBasis: "4%",
    "@media(max-width: 900px)": {
      flexBasis: "5%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "10%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "15%",
    },
  },
  questionDetails: {
    flexBasis: "96%",
    textAlign: "justify",
    "@media(max-width: 900px)": {
      flexBasis: "94%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "90%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "85%",
    },
  },
  commentCard: {
    backgroundColor: "#f9f9f9",
    borderRadius: 4,
    padding: "12px",
    marginBottom: 16,
    border: "1px solid #e0e0e0",
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  userBadge: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    borderRadius: 20,
    padding: "12px 2px",
    fontWeight: 400,
    fontSize: "14px",
    border: `1px solid ${theme.palette.text.secondary}`,
  },
  editIconButton: {
    padding: 8,
  },
  commentText: {
    backgroundColor: "#f9f9f9",
    fontSize: "15px",
    paddingTop: "2px",
    paddingLeft: "2px",
    whiteSpace: "pre-wrap",
  },
  editingField: {
    backgroundColor: "#ffffff",
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  actionButtons: {
    display: "flex",
    gap: 8,
    marginTop: 12,
  },
  cancelBtn: {
    backgroundColor: "transparent",
    color: "#000000",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: "4px 14px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "& .MuiButton-startIcon": {
      marginRight: 4,
    },
  },
  newCommentField: {
    backgroundColor: "#f9f9f9",
    borderRadius: 12,
    padding: "16px",
    border: "1px solid #e0e0e0",
    marginBottom: 16,
  },
  saveCommentBtn: {
    backgroundColor: theme.palette.secondary.main,
    color: "#ffffff",
    borderRadius: 4,
    padding: "4px 14px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#ba5502",
    },
    "& .MuiButton-startIcon": {
      marginRight: 4,
    },
  },
  ghostPlaceholder: {
    backgroundColor: "#ffffff",
    borderRadius: 4,
    padding: "12px",
    marginBottom: 16,
    border: "1px solid #d0d0d0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#000000",
    },
  },
  ghostCommentText: {
    backgroundColor: "#ffffff",
    fontSize: "15px",
    fontWeight: "400",
    paddingTop: "2px",
    paddingBottom: "10px",
    color: "#b0b0b0",
    transition: "all 0.2s ease",
  },
  attachedFilesSection: {
    marginTop: 8,
    marginBottom: 8,
  },
  attachedFilesLabel: {
    fontSize: "14px",
    color: "#666",
    marginBottom: 4,
    display: "block",
  },
  fileBlock: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: "8px 12px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "95%",
    "&:hover": {
      backgroundColor: "#ebebeb",
    },
  },
  fileIcon: {
    marginRight: 8,
    fontSize: 20,
    color: "#666",
  },
  fileName: {
    fontSize: "14px",
    color: "#333",
    marginLeft: 4,
  },
  fileCloseIcon: {
    marginLeft: 8,
    fontSize: 16,
    color: "#999",
    cursor: "pointer",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#666",
    },
  },
  attachFilesRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
  },
  attachFilesLabel: {
    fontSize: "15px",
    color: "#333",
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    textTransform: "none",
    marginLeft: 10,
  },
  dragDropZone: {
    border: "2px dashed #d0d0d0",
    borderRadius: 4,
    padding: "30px 20px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "#fafafa",
    marginTop: 12,
    "&:hover": {
      borderColor: "#999",
      backgroundColor: "#f5f5f5",
    },
  },
  dragDropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#e3f2fd",
  },
  dragDropZoneDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    backgroundColor: "#f0f0f0",
    pointerEvents: "none",
    "&:hover": {
      borderColor: "#d0d0d0",
      backgroundColor: "#f0f0f0",
    },
  },
  uploadIcon: {
    fontSize: 48,
    color: "#999",
    marginBottom: 8,
  },
  uploadText: {
    fontSize: "15px",
    color: "#666",
    marginBottom: 4,
  },
  uploadTextLink: {
    color: theme.palette.secondary.main,
    cursor: "pointer",
  },
  uploadSubText: {
    fontSize: "13px",
    color: "#999",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const CommentInputs = (props) => {
  const { classes } = useStyles();
  const [showNewCommentField, setShowNewCommentField] = useState(false);
  const [allComments, setAllComments] = useState(props.comments);
  const [backdropOpen, setbackDrop] = useState(false);
  const [successSnack, setSuccess] = useState(false);
  const [errorSnack, setError] = useState(false);
  const [currentComment, setCurrentComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedComment, setEditedComment] = useState("");
  const [newCommentUploadedFiles, setNewCommentUploadedFiles] = useState([]);
  const [editCommentUploadedFiles, setEditCommentUploadedFiles] = useState({});
  const { data, error, sendRequest, reqExtra } = useHttp();
  const [fileUploadProgress, setFileUploadProgress] = useState(false);
  const subQuestion = props.subQuestion;
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });
  const [savedFiles, setSavedFiles] = useState(props.files || []);
  const [isDragging, setIsDragging] = useState(false);
  const [editIsDragging, setEditIsDragging] = useState({});
  const [lastProcessedFileName, setLastProcessedFileName] = useState(null);

  useEffect(() => {
    if (data && reqExtra === AppConfig.fileUpload && !error) {
      setFileUploadProgress(false);
      if (data?.data?.fileName && data.data.fileName !== lastProcessedFileName) {
        setLastProcessedFileName(data.data.fileName);
        if (editingCommentId) {
          setEditCommentUploadedFiles((prev) => ({
            ...prev,
            [editingCommentId]: [
              ...(prev[editingCommentId] || []),
              { fileName: data.data.fileName },
            ],
          }));
        } else {
          setNewCommentUploadedFiles((prev) => [
            ...prev,
            { fileName: data.data.fileName },
          ]);
        }
      }
    } else if (error && reqExtra === AppConfig.fileUpload) {
      setFileUploadProgress(false);
    }
  }, [data, error, reqExtra, editingCommentId, lastProcessedFileName]);

  useEffect(() => {
    if (data && reqExtra === AppConfig.deleteFile && !error) {
      setFileUploadProgress(false);
    } else if (error && reqExtra === AppConfig.deleteFile) {
      setFileUploadProgress(false);
    }
  }, [data, error, reqExtra]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSuccess(false);
    setError(false);
    setSnackbarData((prev) => ({ ...prev, open: false }));
  };

  const addNewComment = () => {
    setShowNewCommentField(true);
    setCurrentComment("");
    setNewCommentUploadedFiles([]);
  };

  const cancelNewComment = () => {
    setShowNewCommentField(false);
    setCurrentComment("");
    setNewCommentUploadedFiles([]);
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const generateUniqueFileName = (originalFileName) => {
    const username = getUserName().split("@")[0];
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const uuid = crypto.randomUUID();
    const extension = originalFileName.substring(
      originalFileName.lastIndexOf(".")
    );

    return `${username}-${timestamp}-${uuid}${extension}`;
  };

  const formatDisplayFileName = (fileName) => {
    if (!fileName) return "file";

    if (fileName.length > 25) {
      return fileName.substring(24, 50);
    }

    return fileName;
  };

  const saveNewComment = async () => {
    if (currentComment.trim() === "") return;

    setbackDrop(true);

    const newComment = {
      sortIndex: allComments.filter((c) => c.questionId === props.questionId)
        .length,
      userEmail: getUserName(),
      comment: currentComment,
      questionId: parseInt(props.questionId),
      companyId: parseInt(props.companyId),
      newPartnerFileData: newCommentUploadedFiles.map((file) => ({
        companyId: parseInt(props.companyId),
        questionId: parseInt(props.questionId),
        subQuestionId: subQuestion?.subQuestionId || null,
        fileName: file.fileName,
        fileType: file.fileName.split(".").pop().toLowerCase(),
      })),
    };

    const requestURL = AppConfig.baseUrl + AppConfig.legalComments;

    try {
      let response = await fetch(requestURL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify([newComment]),
      });
      let body = await response.json();
      if (response.status === 200) {
        setSuccess(true);
        setCurrentComment("");
        setNewCommentUploadedFiles([]);
        setShowNewCommentField(false);
        setLastProcessedFileName(null);
        props.formRender();
      } else {
        console.error(body.message);
        setError(true);
      }
      setbackDrop(false);
    } catch (error) {
      console.error(error);
      setError(true);
      setbackDrop(false);
    }
  };

  const startEdit = (commentId, initialText) => {
    setEditingCommentId(commentId);
    setEditedComment(initialText);
    setEditCommentUploadedFiles({ [commentId]: [] }); // ← Initialize empty
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditedComment("");
    setEditCommentUploadedFiles({});
  };

  useEffect(() => {
    setAllComments(props.comments);
    setSavedFiles(props.files || []);
  }, [props.comments, props.files]);

  const saveEditedComment = async (commentId) => {
    setbackDrop(true);
    const requestURL =
      AppConfig.baseUrl + AppConfig.legalComments + "/" + commentId;
    const existingComment = allComments.find((c) => c.commentId === commentId);
    const sortIndex = existingComment ? existingComment.sortIndex : 0;

    const currentFiles = editCommentUploadedFiles[commentId] || [];

    const savedCommentFiles = savedFiles.filter(
      (file) => file.legalCommentId === commentId
    );

    const newFiles = currentFiles.filter(
      (currentFile) =>
        !savedCommentFiles.some(
          (savedFile) => savedFile.fileName === currentFile.fileName
        )
    );

    const newEditedComment = {
      sortIndex: sortIndex,
      userEmail: getUserName(),
      comment: editedComment,
      questionId: parseInt(props.questionId),
      companyId: parseInt(props.companyId),
    };

    if (newFiles.length > 0) {
      newEditedComment.newPartnerFileData = newFiles.map((file) => ({
        companyId: parseInt(props.companyId),
        questionId: parseInt(props.questionId),
        subQuestionId: subQuestion?.subQuestionId || null,
        fileName: file.fileName,
        fileType: file.fileName.split(".").pop().toLowerCase(),
      }));
    }

    try {
      let response = await fetch(requestURL, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify(newEditedComment),
      });
      let body = await response.json();
      if (response.status === 200) {
        setSuccess(true);
        setEditingCommentId(null);
        setEditedComment("");
        setEditCommentUploadedFiles({});
        setLastProcessedFileName(null);
        props.formRender();
      } else {
        console.error(body.message);
        setError(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setbackDrop(false);
    }
  };

  const buildFileViewUrl = (baseUrl, fileName, extensionWithoutDot) => {
    return `${AppConfig.urls.ADMIN}${baseUrl}?fileName=${encodeURIComponent(
      fileName
    )}&extension=${encodeURIComponent(extensionWithoutDot)}`;
  };

  const saveFile = async (files, isEditMode = false, commentId = null) => {
    const fileArray = Object.values(files);
    setFileUploadProgress(true);
    for (const file of fileArray) {
      let fileName = generateUniqueFileName(file.name);
      var extension = fileName.split('.').pop();
      var extensionWithoutDot = extension.toLowerCase();
      const requestURL =
        AppConfig.baseUrl +
        "partners/" +
        props.email +
        "/" +
        AppConfig.fileUpload +
        "?fileExtension=" +
        extensionWithoutDot +
        "&fileName=" +
        fileName;
      try {
        await sendRequest(requestURL, "POST", file, AppConfig.fileUpload);
      } catch (error) {
        setFileUploadProgress(false);
        setSnackbarData({
          message: "Upload failed: " + error.message,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    }
  };

  const handleFileUpload =
    (isEditMode = false, commentId = null) =>
    (event) => {
      const currentFiles = isEditMode
        ? editCommentUploadedFiles[commentId] || []
        : newCommentUploadedFiles;

      const totalFiles = currentFiles.length + event.target.files.length;

      if (totalFiles > FILE_UPLOAD_LIMIT) {
        setSnackbarData({
          message: `Cannot upload more than ${FILE_UPLOAD_LIMIT} files`,
          open: true,
          severity: "warning",
          onClose: onCloseSnackbar,
        });
      } else {
        saveFile(event.target.files, isEditMode, commentId);
      }
    };

  const handleDragEnter = (e, isEditMode = false, commentId = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if already at limit
    const currentFiles = isEditMode
      ? [
          ...(savedFiles.filter((file) => file.legalCommentId === commentId) || []),
          ...(editCommentUploadedFiles[commentId] || []),
        ]
      : newCommentUploadedFiles;
    
    if (currentFiles.length >= FILE_UPLOAD_LIMIT) {
      return; // Don't set dragging state if at limit
    }
    
    if (isEditMode) {
      setEditIsDragging((prev) => ({ ...prev, [commentId]: true }));
    } else {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e, isEditMode = false, commentId = null) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEditMode) {
      setEditIsDragging((prev) => ({ ...prev, [commentId]: false }));
    } else {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, isEditMode = false, commentId = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isEditMode) {
      setEditIsDragging((prev) => ({ ...prev, [commentId]: false }));
    } else {
      setIsDragging(false);
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const currentFiles = isEditMode
        ? [
            ...(savedFiles.filter((file) => file.legalCommentId === commentId) || []),
            ...(editCommentUploadedFiles[commentId] || []),
          ]
        : newCommentUploadedFiles;

      const totalFiles = currentFiles.length + files.length;

      if (totalFiles > FILE_UPLOAD_LIMIT) {
        setSnackbarData({
          message: `Cannot upload more than ${FILE_UPLOAD_LIMIT} files`,
          open: true,
          severity: "warning",
          onClose: onCloseSnackbar,
        });
      } else {
        saveFile(files, isEditMode, commentId);
      }
    }
  };

  const handleZoneClick = (inputId, isEditMode = false, commentId = null) => {
    // Check if already at limit
    const currentFiles = isEditMode
      ? [
          ...(savedFiles.filter((file) => file.legalCommentId === commentId) || []),
          ...(editCommentUploadedFiles[commentId] || []),
        ]
      : newCommentUploadedFiles;
    
    if (currentFiles.length >= FILE_UPLOAD_LIMIT) {
      setSnackbarData({
        message: `Maximum of ${FILE_UPLOAD_LIMIT} files already reached`,
        open: true,
        severity: "warning",
        onClose: onCloseSnackbar,
      });
      return; // Don't trigger file input
    }
    
    document.getElementById(inputId).click();
  };

  const deleteFile = async (fileName) => {
    if (fileName) {
      const requestURL =
        AppConfig.baseUrl +
        "partners/" +
        props.email +
        "/" +
        AppConfig.fileDelete +
        "/" +
        fileName;
      sendRequest(requestURL, "DELETE", null, AppConfig.deleteFile);
    }
  };

  const handleFileRemove = async (
    file,
    isEditMode = false,
    commentId = null
  ) => {
    setFileUploadProgress(true);
    let fileName = file.fileName;

    if (isEditMode) {
      setEditCommentUploadedFiles((prev) => ({
        ...prev,
        [commentId]: prev[commentId].filter(
          (item) => item.fileName !== fileName
        ),
      }));
    } else {
      setNewCommentUploadedFiles(
        newCommentUploadedFiles.filter((item) => item.fileName !== fileName)
      );
    }

    deleteFile(fileName);
  };

  const handleChipOnClick = (fileName) => {
    var extension = fileName.split('.').pop();
    var extensionWithoutDot = extension.toLowerCase();

    const PDF_URL = buildFileViewUrl(
      AppConfig.urls.VIEW_PDF,
      fileName,
      extensionWithoutDot
    );
    const IMAGE_URL = buildFileViewUrl(
      AppConfig.urls.VIEW_IMAGE,
      fileName,
      extensionWithoutDot
    );

    if (extension === "pdf") {
      window.open(PDF_URL, "_blank");
    } else if (extension === "jpg") {
      window.open(IMAGE_URL, "_blank");
    } else if (extension === "jpeg") {
      window.open(IMAGE_URL, "_blank");
    } else if (extension === "png") {
      window.open(IMAGE_URL, "_blank");
    } else {
      const url = `${AppConfig.FileServiceURL}${fileName}?fileExtension=${extensionWithoutDot}`;
      window.open(url, "_blank");
    }
  };

  const questionComments = allComments.filter(
    (comment) => comment.questionId === props.questionId
  );

  const canAddComment =
    props.questionId === 80 && checkIfRoleExists(roles.legalApprover);

  const showFileAttachment =
    subQuestion?.subQuestionId === LEGAL_CHOOSE_FILE_SUBQUESTION_ID;

  return (
    <div className={classes.parentDiv}>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>
      <Snackbar
        open={successSnack}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity="success">
          Success!
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={errorSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          Failed!
        </Alert>
      </Snackbar>
      <Snackbar
        open={snackbarData.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={snackbarData.severity}>
          {snackbarData.message}
        </Alert>
      </Snackbar>
      
      {/* Legal opinion*/}
      <div className={classes.questionParent}>
        <div className={classes.questionIndex}></div>
        <div className={classes.questionDetails}>
          {questionComments.map((item, key) => {
            const isEditing = editingCommentId === item.commentId;

            const savedCommentFiles = savedFiles.filter(
              (file) => file.legalCommentId === item.commentId
            );

            const commentFiles = isEditing
              ? [
                  ...savedCommentFiles,
                  ...(editCommentUploadedFiles[item.commentId] || []),
                ]
              : savedCommentFiles;
              
            const isEditFileLimitReached = commentFiles.length >= FILE_UPLOAD_LIMIT;
            
            return (
              <div key={item.commentId} className={classes.commentCard}>
                <div className={classes.commentHeader}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Tooltip title={item.userEmail}>
                      <Chip
                        icon={<Avatar sx={{ width: 20, height: 20, fontSize: 12 }} />}
                        className={classes.userBadge}
                        label={item.userName}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Tooltip>
                  </div>
                  {!isEditing && (
                    <IconButton
                      size="small"
                      className={classes.editIconButton}
                      onClick={() => startEdit(item.commentId, item.comment)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>

                {isEditing ? (
                  <>
                    <TextField
                      value={editedComment}
                      onChange={(e) => setEditedComment(e.target.value)}
                      variant="outlined"
                      multiline
                      minRows={3}
                      fullWidth
                      className={classes.editingField}
                    />

                    {showFileAttachment && (
                      <>
                        <div
                          className={`${classes.dragDropZone} ${
                            editIsDragging[item.commentId]
                              ? classes.dragDropZoneActive
                              : ""
                          } ${
                            isEditFileLimitReached
                              ? classes.dragDropZoneDisabled
                              : ""
                          }`}
                          onDragEnter={(e) => handleDragEnter(e, true, item.commentId)}
                          onDragOver={handleDragOver}
                          onDragLeave={(e) => handleDragLeave(e, true, item.commentId)}
                          onDrop={(e) => handleDrop(e, true, item.commentId)}
                          onClick={() => handleZoneClick(`edit-file-input-${item.commentId}`, true, item.commentId)}
                        >
                          <CloudUploadIcon className={classes.uploadIcon} />
                          <div className={classes.uploadText}>
                            <span className={classes.uploadTextLink}>
                              {isEditFileLimitReached ? "Maximum files reached" : "Click to upload"}
                            </span>
                            {!isEditFileLimitReached && " or drag and drop"}
                          </div>
                          <div className={classes.uploadSubText}>
                            {isEditFileLimitReached 
                              ? `(${commentFiles.length}/${FILE_UPLOAD_LIMIT} files uploaded)`
                              : "PDF, JPEG, PNG, JPG (max 5 files)"
                            }
                          </div>
                        </div>
                        <input
                          type="file"
                          multiple
                          id={`edit-file-input-${item.commentId}`}
                          className={classes.fileInput}
                          onChange={handleFileUpload(true, item.commentId)}
                          disabled={isEditFileLimitReached}
                        />
                      </>
                    )}

                    {commentFiles.length > 0 && (
                      <div className={classes.attachedFilesSection}>
                        <span className={classes.attachedFilesLabel}>
                          Attached files:
                        </span>
                        <div>
                          {commentFiles.map((file, idx) => {
                            const isNewFile = !savedCommentFiles.some(
                              (savedFile) =>
                                savedFile.fileName === file.fileName
                            );

                            return (
                              <div key={idx} className={classes.fileBlock}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flex: 1,
                                  }}
                                  onClick={() =>
                                    handleChipOnClick(file.fileName)
                                  }
                                >
                                  <InsertDriveFileIcon
                                    className={classes.fileIcon}
                                  />
                                  <span className={classes.fileName}>
                                    {formatDisplayFileName(file.fileName)}
                                  </span>
                                </div>
                                {isNewFile && (
                                  <CloseIcon
                                    className={classes.fileCloseIcon}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFileRemove(
                                        file,
                                        true,
                                        item.commentId
                                      );
                                    }}
                                  />
                                )}
                                {fileUploadProgress && (
                                  <LinearProgress className={classes.linearProgress} color="secondary" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className={classes.actionButtons}>
                      <Button
                        className={classes.saveCommentBtn}
                        variant="contained"
                        size="small"
                        onClick={() => saveEditedComment(item.commentId)}
                      >
                        Save
                      </Button>
                      <Button
                        className={classes.cancelBtn}
                        variant="outlined"
                        size="small"
                        onClick={cancelEdit}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={classes.commentText}>{item.comment}</div>

                    {commentFiles.length > 0 && (
                      <div className={classes.attachedFilesSection}>
                        <span className={classes.attachedFilesLabel}>
                          Attached files:
                        </span>
                        <div>
                          {commentFiles.map((file, idx) => (
                            <div
                              key={idx}
                              className={classes.fileBlock}
                              onClick={() => handleChipOnClick(file.fileName)}
                            >
                              <InsertDriveFileIcon
                                className={classes.fileIcon}
                              />
                              <span className={classes.fileName}>
                                {formatDisplayFileName(file.fileName)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {((questionComments.length === 0 &&
            !showNewCommentField &&
            canAddComment) ||
            (canAddComment &&
              questionComments.length > 0 &&
              !showNewCommentField)) && (
            <div className={classes.ghostPlaceholder} onClick={addNewComment}>
              <div className={classes.ghostCommentText}>
                Add your comment...
              </div>
            </div>
          )}

          {showNewCommentField && (
            <div className={classes.newCommentField}>
              <TextField
                placeholder="Add your comment..."
                value={currentComment}
                onChange={(e) => setCurrentComment(e.target.value)}
                variant="outlined"
                multiline
                minRows={3}
                fullWidth
                className={classes.editingField}
              />

              {showFileAttachment && (
                <>
                  <div
                    className={`${classes.dragDropZone} ${
                      isDragging ? classes.dragDropZoneActive : ""
                    } ${
                      newCommentUploadedFiles.length >= FILE_UPLOAD_LIMIT
                        ? classes.dragDropZoneDisabled
                        : ""
                    }`}
                    onDragEnter={(e) => handleDragEnter(e, false)}
                    onDragOver={handleDragOver}
                    onDragLeave={(e) => handleDragLeave(e, false)}
                    onDrop={(e) => handleDrop(e, false)}
                    onClick={() => handleZoneClick("new-comment-file-input", false)}
                  >
                    <CloudUploadIcon className={classes.uploadIcon} />
                    <div className={classes.uploadText}>
                      <span className={classes.uploadTextLink}>
                        {newCommentUploadedFiles.length >= FILE_UPLOAD_LIMIT 
                          ? "Maximum files reached" 
                          : "Click to upload"}
                      </span>
                      {newCommentUploadedFiles.length < FILE_UPLOAD_LIMIT && " or drag and drop"}
                    </div>
                    <div className={classes.uploadSubText}>
                      {newCommentUploadedFiles.length >= FILE_UPLOAD_LIMIT
                        ? `(${newCommentUploadedFiles.length}/${FILE_UPLOAD_LIMIT} files uploaded)`
                        : "PDF, JPEG, PNG, JPG (max 5 files)"
                      }
                    </div>
                  </div>
                  <input
                    type="file"
                    multiple
                    id="new-comment-file-input"
                    className={classes.fileInput}
                    onChange={handleFileUpload(false)}
                    disabled={newCommentUploadedFiles.length >= FILE_UPLOAD_LIMIT}
                  />
                </>
              )}

              {newCommentUploadedFiles.length > 0 && (
                <div className={classes.attachedFilesSection}>
                  <span className={classes.attachedFilesLabel}>
                    Attached files:
                  </span>
                  <div>
                    {newCommentUploadedFiles.map((file, idx) => (
                      <div key={idx} className={classes.fileBlock}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flex: 1,
                          }}
                          onClick={() => handleChipOnClick(file.fileName)}
                        >
                          <InsertDriveFileIcon className={classes.fileIcon} />
                          <span className={classes.fileName}>
                            {formatDisplayFileName(file.fileName)}
                          </span>
                        </div>
                        <CloseIcon
                          className={classes.fileCloseIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileRemove(file, false);
                          }}
                        />
                      </div>
                    ))}
                    {fileUploadProgress && (
                                        <LinearProgress className={classes.linearProgress} color="secondary" />
                                      )}
                  </div>
                </div>
              )}

              <div className={classes.actionButtons}>
                <Button
                  className={classes.saveCommentBtn}
                  variant="contained"
                  size="small"
                  onClick={saveNewComment}
                  disabled={currentComment.trim() === ""}
                >
                  Save
                </Button>
                <Button
                  className={classes.cancelBtn}
                  variant="outlined"
                  size="small"
                  onClick={cancelNewComment}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentInputs;
